import React from 'react';
import { CheckCircle2, ChevronDown, Lock, ListChecks, ClipboardCheck } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';

// "1 activity · 1 quiz" summary with icons.
const WeekCounts = ({ period, total }) => {
  const quizzes = (period.activities || []).filter((a) => a.status !== 'draft' && a.has_quiz).length;
  return (
    <span className="inline-flex items-center gap-3 text-xs text-zinc-500" data-testid={`learner-week-counts-${period.period_id}`}>
      <span className="inline-flex items-center gap-1"><ListChecks className="h-3.5 w-3.5" />{total} activit{total === 1 ? 'y' : 'ies'}</span>
      {quizzes > 0 && (
        <span className="inline-flex items-center gap-1 text-violet-300" data-testid={`learner-week-quizzes-${period.period_id}`}>
          <ClipboardCheck className="h-3.5 w-3.5" />{quizzes} quiz{quizzes === 1 ? '' : 'zes'}
        </span>
      )}
    </span>
  );
};

export default function LearnerWeekCard({ period, index, color, open, onToggle, completed, total, isEnrolled, children }) {
  const pct = total ? Math.round((completed / total) * 100) : 0;
  const done = total > 0 && completed === total;
  if (period.locked) {
    return (
      <div className="rounded-2xl border border-zinc-800/70 bg-zinc-900/40 flex items-center gap-4 p-4 sm:p-5 opacity-70" data-testid={`learner-week-${period.period_id}`}>
        <span className="h-11 w-11 shrink-0 rounded-xl flex items-center justify-center bg-zinc-800 text-zinc-500"><Lock className="h-4 w-4" /></span>
        <span className="flex-1 min-w-0">
          <span className="block text-zinc-300 font-semibold truncate">{period.title}</span>
          <span className="block text-xs text-zinc-500 mt-0.5" data-testid={`learner-week-locked-${period.period_id}`}>Locked · <WeekCounts period={period} total={total} /></span>
        </span>
      </div>
    );
  }
  return (
    <div className={`rounded-2xl border bg-zinc-900/70 transition-colors ${open ? 'border-zinc-700' : 'border-zinc-800 hover:border-zinc-700'}`} data-testid={`learner-week-${period.period_id}`}>
      <Collapsible open={open} onOpenChange={onToggle}>
        <CollapsibleTrigger asChild>
          <button type="button" className="w-full text-left flex items-center gap-4 p-4 sm:p-5" data-testid={`learner-week-toggle-${period.period_id}`}>
            <span className="h-11 w-11 shrink-0 rounded-xl flex items-center justify-center font-semibold text-sm" style={done ? { backgroundColor: 'rgba(20,184,166,0.15)', color: '#2dd4bf' } : { backgroundColor: `${color}22`, color }}>
              {done ? <CheckCircle2 className="h-5 w-5" /> : index + 1}
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-white font-semibold truncate">{period.title}</span>
              {period.description && <span className="block text-sm text-zinc-400 mt-0.5 line-clamp-1" data-testid={`period-summary-${period.period_id}`}>{period.description}</span>}
              {isEnrolled && total > 0 && (
                <span className="flex items-center gap-3 mt-2">
                  <span className="h-1.5 flex-1 max-w-[220px] rounded-full bg-zinc-800 overflow-hidden">
                    <span className="block h-full rounded-full transition-[width] duration-500" style={{ width: `${pct}%`, backgroundColor: done ? '#14b8a6' : color }} />
                  </span>
                  <span className="text-xs text-zinc-500" data-testid={`learner-week-progress-${period.period_id}`}>{completed}/{total} done</span>
                </span>
              )}
              {total > 0 && <span className="block mt-1.5"><WeekCounts period={period} total={total} /></span>}
            </span>
            <ChevronDown className={`h-5 w-5 text-zinc-500 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 sm:px-5 pb-5 sm:pl-[4.75rem] space-y-3">{children}</div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
